<template>
	<Modal
		style="background-color: white; top: 45px"
		class="overflow-auto"
	>
		<div class="instruction mt-[600px]">
			<div class="heading">
				<i
					class="fas fa-angle-left"
					@click="$emit('previous')"
				/>
				<h1>{{ $t('antesDeComecarmos') }}</h1>
			</div>
			<div>
				<p>
					{{ $t('verifiqueAsColunas') }}
				</p>
				<a @click="downloadExemplo">{{ $t('baixeArquivoModelo') }}</a>
			</div>

			<h2 class="text-2xl text-[#012E4B] font-bold">Dados cadastrais</h2>

			<table cellspacing="0">
				<thead>
					<tr>
						<th
							v-for="(coluna, index) in colunas"
							:key="coluna.nome + index"
							class="text-start"
						>
							{{ coluna.nome }}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td
							v-for="(coluna, index) in colunas"
							:key="coluna.exemplo + index + uuidv4()"
						>
							{{ coluna.exemplo }}
						</td>
					</tr>
				</tbody>
			</table>

			<div class="instruction-container">
				<p
					v-for="coluna in colunas"
					:key="coluna.key"
					style="padding: 0.15rem 0"
				>
					{{ $t('column') }}
					<strong>{{ `${coluna.nome} ${coluna.unidade || ''}` }}</strong>
					{{ coluna.descricao.replace('Coluna', '') }}
					{{ $t('emFormato') }}
					<strong>{{ coluna.formato === 'number' ? 'Número' : 'Texto' }}</strong>
					<span
						v-if="coluna.obrigatorio"
						class="bg-[#01395E] text-[#FEFEFE] px-2 py-0.5 rounded-full ml-2 text-xs font-bold"
						>obrigatório</span
					>
				</p>
			</div>

			<p>
				Os campos opcionais que não forem preenchidos (célula vazia) serão atribuídos como “Não
				informados”
			</p>

			<h2 class="text-2xl text-[#012E4B] font-bold">Dados de processamento</h2>

			<table cellspacing="0">
				<thead>
					<tr>
						<th
							v-for="(coluna, index) in colunasProcessamento"
							:key="coluna.nome + index"
							class="text-start"
						>
							{{ coluna.nome }}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td
							v-for="(coluna, index) in colunasProcessamento"
							:key="coluna.exemplo + index + uuidv4()"
						>
							{{ coluna.exemplo }}
						</td>
					</tr>
				</tbody>
			</table>

			<div class="instruction-container">
				<p
					v-for="coluna in colunasProcessamento"
					:key="coluna.key"
					style="padding: 0.15rem 0"
				>
					{{ $t('column') }}
					<strong>{{ `${coluna.nome} ${coluna.unidade || ''}` }}</strong>
					{{ coluna.descricao.replace('Coluna', '') }}
					{{ $t('emFormato') }}
					<strong>{{ coluna.formato === 'number' ? 'Número' : 'Texto' }}</strong>
				</p>
			</div>

			<div class="flex 2-full justify-between items-center mb-[40px]">
				<Checkbox
					v-model="conscent"
					:label="$t('naoMostrarNovamente')"
				/>
				<Button
					:label="$t('continue')"
					style="width: fit-content; float: right"
					@click="$emit('continue')"
				/>
			</div>
		</div>
		<Modal v-if="modalAux">
			<div class="bg-[#fefefe] p-8 rounded-md max-w-[33vw]">
				<h1 class="text-[#01395E] font-bold text-2xl mb-9">Atenção</h1>
				<p>
					Os campos apresentados deverão estar contemplados em apenas um arquivo .csv com tamanho
					até 1,5MB (4500 linhas) e todas as variáveis devem estar em uma mesma linha, conforme
					ilustrado a seguir:
				</p>
				<img
					src="../../assets/exemplo_base_historica.gif"
					class="my-9"
					alt=""
				/>
				<div class="flex gap-4">
					<Button
						label="baixar arquivo modelo"
						outlined
						color="trv-primary"
						@click="downloadExemplo"
					/>
					<Button
						label="cadastrar"
						@click="modalAux = false"
					/>
				</div>
			</div>
		</Modal>
	</Modal>
</template>

<script setup>
import { v4 as uuidv4 } from 'uuid'
import { useLocalStorage } from '@vueuse/core'
import { ref, toRefs } from 'vue'
import Button from '../Button/Button.vue'
import Checkbox from '../Checkbox/Checkbox.vue'
import Modal from '../Modal/Modal.vue'

import download from 'downloadjs'

const props = defineProps({
	colunas: {
		type: Array,
		defualt: () => [],
	},
	colunasProcessamento: {
		type: Array,
		default: () => [],
	},
	chave: { type: String, default: 'default' },
})

const { chave } = toRefs(props)

const conscent = useLocalStorage(`conscent-${chave.value}`, false)

const modalAux = ref(true)

const downloadExemplo = () => {
	const items = Array(5).fill(
		props.colunas.concat(props.colunasProcessamento).map((m) => m.exemplo),
	)

	const header = props.colunas
		.concat(props.colunasProcessamento)
		.map((m) => m.nome.normalize('NFD').replace(/[^a-zA-Z\s]/g, ''))
	const csv = [
		header, // header row first
		...items,
	]
		.map((m) => m.join(';'))
		.join('\r\n')

	download(csv, 'Exemplo Base Historica.csv', 'text/csv')
}
</script>

<style scoped>
table {
	white-space: nowrap;
}

.instruction-container {
	background-color: #f1f1f1;
	padding: 1rem;
	border: 1.5px solid #737576;
	border-radius: 5px;
}

.heading {
	display: flex;
	flex-direction: row;
	gap: 0.5rem;
	align-items: center;
}

thead tr {
	background-color: #ccd7df;
}

th {
	color: #01395e;
}

th,
td {
	padding: 0 0.5rem;
}

td {
	color: #272728;
	font-size: 0.9rem;
}

i {
	font-size: 1.3rem;
	cursor: pointer;
}

h1,
a,
p {
	margin: 0;
}

.instruction {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}
</style>
